import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  price?: React.ReactNode;
  /** Position i of n in the current (sorted, filtered) list, for "‹ 2 of 6 ›". */
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Right-side position drawer (Oct 2026). Portrait panel over a dimmed page:
 * the ledger stays readable behind it, ↑/↓ (or k/j) step through positions,
 * Esc / ✕ / a click on the dim area close it. Full-screen sheet on phones.
 */
const PositionDrawer: React.FC<Props> = ({ title, subtitle, price, index, count, onPrev, onNext, onClose, children }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);

  // focus the panel on open, give focus back on close; lock page scroll
  useEffect(() => {
    lastFocus.current = document.activeElement as HTMLElement;
    panelRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      lastFocus.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // leave keys alone while another dialog (Manage, coach) is open or the user types
      if (document.querySelector('[role="dialog"]:not(.pdr)')) return;
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); onPrev(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  return createPortal(
    <div className="pdr-root">
      <div className="pdr-dim" onClick={onClose} aria-hidden="true" />
      <div className="pdr" role="dialog" aria-modal="true" aria-label="Position details" ref={panelRef} tabIndex={-1}>
        <header className="pdr-head">
          <div className="pdr-id">
            <div className="pdr-title">{title}</div>
            {subtitle && <div className="pdr-sub">{subtitle}</div>}
          </div>
          {price && <div className="pdr-price">{price}</div>}
          <div className="pdr-nav">
            <button type="button" className="pdr-ib" onClick={onPrev} disabled={index <= 0} aria-label="Previous position (↑)">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 10 4-4 4 4" /></svg>
            </button>
            <span className="pdr-count">{index + 1} of {count}</span>
            <button type="button" className="pdr-ib" onClick={onNext} disabled={index >= count - 1} aria-label="Next position (↓)">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
            </button>
            <button type="button" className="pdr-ib" onClick={onClose} aria-label="Close (Esc)">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg>
            </button>
          </div>
        </header>
        <div className="pdr-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default PositionDrawer;
